import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ConversionPreview } from "@/components/conversion-preview/ConversionPreview";
import { depositFormSchema, DepositFormValues } from "./schemas/depositForm.schema";
import { useCreateSaccoDepositWithDebit, useUpdateSaccoDepositWithDebit } from "@saccoHooks";
import { useFetchAccounts } from "@accountHooks";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { supabase } from "@/integrations/supabase/client";
import {SaccoDeposit} from "@/@types/sacco.models.ts";

interface DepositFormProps {
  membershipId: string;
  nativeCurrency?: string;
  deposit?: SaccoDeposit;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DepositForm({
  membershipId,
  nativeCurrency = "KES",
  deposit,
  open,
  onOpenChange,
  onSuccess,
}: DepositFormProps) {
  const isEdit = !!deposit;
  // 9.1 — edit mode cases
  const editHasLinkedTransaction = isEdit && !!deposit?.transaction_id;
  const editIsLegacy = isEdit && !deposit?.transaction_id;

  // 9.1 — fetch linked transaction to discover its account_id (only when editing with a transaction_id)
  const { data: linkedTransaction } = useQuery({
    queryKey: ["transaction", deposit?.transaction_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("account_id")
        .eq("id", deposit!.transaction_id!)
        .single();
      if (error) throw error;
      return data as { account_id: string };
    },
    enabled: editHasLinkedTransaction,
  });

  // 8.4 — new hooks replace old useCreateDeposit / useUpdateDeposit
  const createDeposit = useCreateSaccoDepositWithDebit();
  const updateDeposit = useUpdateSaccoDepositWithDebit();

  // 8.1 — source account list
  const {
    data: accounts,
    isLoading: isLoadingAccounts,
    error: accountsError,
  } = useFetchAccounts();

  // 8.2 — currency list
  const {
    data: currencyList,
    isLoading: isLoadingCurrencies,
    error: currenciesError,
    refetch: refetchCurrencies,
  } = useCurrencyList();

  // 8.3 — exchange rates (base USD; getRate handles cross-pair conversion)
  const {
    getRate,
    isLoading: isLoadingRates,
    error: ratesError,
  } = useExchangeRates();

  // Track whether the user manually changed the deposit currency
  const currencyManuallyChanged = useRef(false);
  // Track the currently-selected source account id for balance helper text
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  const form = useForm<DepositFormValues>({
    resolver: zodResolver(depositFormSchema),
    defaultValues: {
      amount: "" as unknown as number,
      deposit_date: "",
      note: "",
      source_account_id: "",
      deposit_currency: "",
    },
  });

  // Reset form whenever the dialog opens or switches between deposits
  useEffect(() => {
    if (open) {
      currencyManuallyChanged.current = false;
      form.reset({
        amount: deposit?.amount ? Number(deposit.amount) : ("" as unknown as number),
        deposit_date: deposit?.deposit_date ?? "",
        note: deposit?.note ?? "",
        source_account_id: "",
        deposit_currency: "",
      });
      setSelectedAccountId("");
    }
  }, [open, deposit, form]);

  // 9.1 — once the linked transaction loads, pre-populate the source account dropdown
  useEffect(() => {
    if (editHasLinkedTransaction && linkedTransaction?.account_id) {
      form.setValue("source_account_id", linkedTransaction.account_id);
      setSelectedAccountId(linkedTransaction.account_id);
    }
  }, [editHasLinkedTransaction, linkedTransaction, form]);

  const isPending = createDeposit.isPending || updateDeposit.isPending;

  // Derived values
  const selectedAccount = accounts?.find((a) => a.id === selectedAccountId);
  const sourceAccountNativeCurrency = selectedAccount?.currency ?? "";
  const depositCurrency = form.watch("deposit_currency") ?? "";
  const amountValue = form.watch("amount");
  const parsedAmount = Number(amountValue) || 0;

  // 8.2 — auto-default deposit_currency to the source account's native currency
  // when the user has not manually changed it
  useEffect(() => {
    if (!currencyManuallyChanged.current && sourceAccountNativeCurrency) {
      form.setValue("deposit_currency", sourceAccountNativeCurrency);
    }
  }, [sourceAccountNativeCurrency, form]);

  // 8.3 — show ConversionPreview when currencies differ, amount > 0, and amount is not empty
  const showConversionPreview =
    !!depositCurrency &&
    !!sourceAccountNativeCurrency &&
    depositCurrency !== sourceAccountNativeCurrency &&
    parsedAmount > 0 &&
    amountValue !== ("" as unknown as number) &&
    amountValue !== 0;

  async function onSubmit(values: DepositFormValues) {
    // 8.1 — enforce required source_account_id on create
    if (!isEdit && !values.source_account_id) {
      form.setError("source_account_id", {
        type: "manual",
        message: "Source account is required",
      });
      return;
    }

    // 8.3 — block submit when rate fetch failed in a cross-currency scenario
    if (
      showConversionPreview &&
      !isLoadingRates &&
      ratesError
    ) {
      toast.error("Cannot submit: exchange rate is not available");
      return;
    }

    // 8.2 — block submit when currency list failed to load (create mode)
    if (!isEdit && currenciesError && !currencyList) {
      toast.error("Cannot submit: currency list has not loaded");
      return;
    }

    try {
      if (isEdit) {
        // 8.4 — update path
        await updateDeposit.mutateAsync({
          depositId: deposit.id,
          membershipId,
          amount: values.amount,
          depositDate: values.deposit_date,
          note: values.note ?? null,
        });
        toast.success("Deposit updated successfully");
      } else {
        // 8.4 — create path
        await createDeposit.mutateAsync({
          membershipId,
          sourceAccountId: values.source_account_id!,
          amount: values.amount,
          depositCurrency: values.deposit_currency || sourceAccountNativeCurrency,
          depositDate: values.deposit_date,
          note: values.note ?? null,
        });
        toast.success("Deposit recorded successfully");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      toast.error(
        message ??
          (isEdit ? "Failed to update deposit" : "Failed to record deposit")
      );
    }
  }

  // Source account dropdown section rendered below handles create/edit logic

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Deposit" : "Add Deposit"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

            {/* 8.1 / 9.1 — Source account dropdown */}
            {/* Create mode: required; edit with transaction_id: disabled/read-only; edit legacy: optional */}
            <FormField
              control={form.control}
              name="source_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Source Account {!isEdit && "*"}
                  </FormLabel>
                  <FormControl>
                    {accountsError ? (
                      <div className="space-y-1">
                        <Select disabled>
                          <SelectTrigger>
                            <SelectValue placeholder="Failed to load accounts" />
                          </SelectTrigger>
                        </Select>
                        <p className="text-sm text-destructive">
                          Could not load accounts. Please try again later.
                        </p>
                      </div>
                    ) : (
                      <Select
                        disabled={isLoadingAccounts || editHasLinkedTransaction}
                        value={field.value ?? ""}
                        onValueChange={(val) => {
                          field.onChange(val);
                          setSelectedAccountId(val);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingAccounts
                                ? "Loading accounts…"
                                : editIsLegacy
                                ? "No source account (legacy)"
                                : "Select source account"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {(accounts ?? []).map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.nickname ?? account.name} ({account.currency})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </FormControl>
                  {/* Balance helper text */}
                  {selectedAccount && !accountsError && (
                    <p className="text-sm text-muted-foreground">
                      {selectedAccount.balance.toFixed(2)} {selectedAccount.currency}
                    </p>
                  )}
                  {/* 9.1 — hint that source account cannot be changed in edit mode */}
                  {editHasLinkedTransaction && (
                    <p className="text-sm text-muted-foreground">
                      Source account cannot be changed for linked deposits.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 8.2 — Deposit currency selector (create mode only) */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="deposit_currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deposit Currency *</FormLabel>
                    <FormControl>
                      {currenciesError && !currencyList ? (
                        <div className="space-y-1">
                          <Select disabled>
                            <SelectTrigger>
                              <SelectValue placeholder="Failed to load currencies" />
                            </SelectTrigger>
                          </Select>
                          <p className="text-sm text-destructive">
                            Could not load currencies.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => refetchCurrencies()}
                          >
                            Retry
                          </Button>
                        </div>
                      ) : (
                        <Select
                          disabled={isLoadingCurrencies}
                          value={field.value ?? ""}
                          onValueChange={(val) => {
                            currencyManuallyChanged.current = true;
                            field.onChange(val);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                isLoadingCurrencies
                                  ? "Loading currencies…"
                                  : "Select currency"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {(currencyList ?? []).map((c) => (
                              <SelectItem key={c} value={c}>
                                {c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Amount */}
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {`Amount (${depositCurrency || nativeCurrency}) *`}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Deposit date */}
            <FormField
              control={form.control}
              name="deposit_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deposit Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 8.3 — ConversionPreview */}
            {showConversionPreview && (
              <>
                {isLoadingRates ? (
                  <p className="text-sm text-muted-foreground">
                    Loading exchange rate…
                  </p>
                ) : ratesError ? (
                  <p className="text-sm text-destructive">
                    Could not load exchange rate. Submission is disabled until
                    the rate is available.
                  </p>
                ) : (
                  <ConversionPreview
                    amount={parsedAmount}
                    fromCurrency={depositCurrency}
                    toCurrency={sourceAccountNativeCurrency}
                    getRate={getRate}
                    isLoadingRates={isLoadingRates}
                  />
                )}
              </>
            )}

            {/* Note */}
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional note"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              {/* 8.4 — disable while pending; 8.2/8.3 — disable when currencies/rates unavailable */}
              <Button
                type="submit"
                disabled={
                  isPending ||
                  (!isEdit && !!currenciesError && !currencyList) ||
                  (showConversionPreview && !isLoadingRates && !!ratesError)
                }
              >
                {isPending ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
